import { useState, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useServices } from "@/services-context";
import close from "@/assets/close.svg";
import noImage from "@/assets/no-image.jpg";
import styles from "./index.module.scss";
import FixQuantity from "@/elements-react/fix-quantity";
import ProductSpecifications from "@/elements-react/product-specifications";
import ConfirmDialog from "@/elements-react/confirm-dialog";
import CartProductNotify from "@/elements-react/cart-product-notify";
import CartProductNotifyTitle from "@/elements-react/cart-product-notify-title";
import ExchangeOffer from "@/elements-react/exchange-offer";
import PriceValue from "@/elements-react/price-value";
import type { B2cCartProduct, B2cProductPrice } from "@/generated/graphql/b2c";
import { stockType } from "@/constants/product-stock";
import { FixButton } from "@/elements-react/fix-button";
import { ImageResizer } from "@/util/image-resizer.ts";
import { Gtag } from "@/services/gtag.ts";
import { Icon } from "@/elements-react/fix-icon";
import FixImage from "@/elements-react/fix-image";
import { CART_LISTENER_NAME } from "@/context/cart.ts";
import { useLayoutType } from "@/util-react/layout.tsx";
import { createProductUrl } from "@/util/category.ts";
import { getCategoriesEcommerceItems, getItemName, googleEvent } from "@/helpers/google-events.ts";
import {getSpecifications} from "@/helpers/product-manager.ts";

export interface CartItemProps {
  product: B2cCartProduct;
  singleProduct?: boolean;
  layout?: string;
  categories?: any;
}

const CartItem = ({ product, singleProduct = true, layout, categories }: CartItemProps) => {
  const { t } = useTranslation();
  const services = useServices([CART_LISTENER_NAME]);
  const { layoutStyleType } = useLayoutType();

  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [quantity, setQuantity] = useState(product.quantity ?? 0);
  const [specificationsOpened, setSpecificationsOpened] = useState(false);
  const [pricesOpened, setPricesOpened] = useState(false);
  const [isOverMaxQuantity, setIsOverMaxQuantity] = useState(false);

  const currentPrice = useMemo(() => product.product.prices.find((item) => item.id === product.priceId), [product]);

  const price = useMemo(() => {
    const discount = 0;
    const totalAmount = Number(product.price?.amount) * quantity;
    return {
      amount: totalAmount,
      discount: totalAmount * (discount / 100),
      discountPercent: discount,
      price: product.price?.amount,
      total: totalAmount - discount,
    };
  }, [product, quantity]);

  const deliveryTermsRange = (price: B2cProductPrice | undefined) => {
    if (!price) return "-";
    const isShipNextBusinessDay = [stockType.localStock, stockType.localDistributorStock].includes(price?.stockType ?? "");
    if (isShipNextBusinessDay) {
      return `- ${t("bs6x10iqxu790209.next-business-day", "Next business day")}`;
    }
    if (price?.deliveryTermStandardMin === price?.deliveryTermStandardMax) {
      return t("rfje423vfw3fj4hv.delivery-term", "{{deliveryTerm}} days", { deliveryTerm: price?.deliveryTermStandardMin });
    }
    return t("rfje21tj4m4fj4hv.delivery-terms-range", "{{minTerm}}-{{maxTerm}} days", {
      minTerm: price?.deliveryTermStandardMin,
      maxTerm: price?.deliveryTermStandardMax,
    });
  };

  const specifications = useMemo(() => getSpecifications(product.product, t), [product.product, t]);

  const imgUrl = useMemo(() => {
    const images = product.product.productAssets
      ?.filter((asset) => asset.type === "image")
      .map((asset) => ({
        ...asset,
        url: ImageResizer.getProductImageUrl(asset.path, "160x160"),
      }));
    return images?.length ? images[0]?.url : "";
  }, [product]);

  const beforeSetQuantity = (newQuantity: number) => {
    if (product.maxOrderQuantity && newQuantity > product.maxOrderQuantity) {
      if (product.product?.exchangesFinal && product.product?.exchangesFinal.length === 1) {
        setIsOverMaxQuantity(true);
      } else {
        services.alerts.addMessages([t("2k1l3hf6f0682ddk.max-quantity-exceeded", "Quantity exceeds available stock")], "warning");
      }
      return false;
    }
    setIsOverMaxQuantity(false);
    return true;
  };

  const handleQuantityChange = (newQuantity: number) => {
    Gtag.pushEvent("update_quantity", { quantity: newQuantity, cartProductId: product.id, dispatchTime: "" });
    setQuantity(newQuantity);

    services.cart.updateItem({
      productId: product!.product.id,
      cartProductId: product!.id,
      quantity: newQuantity,
      priceId: product!.priceId,
      wholeCartValidation: true,
    });
  };

  const changePrice = (priceId: string) => {
    const dispatchTime = currentPrice || null;
    Gtag.pushEvent("dispatch_time_selection", {
      quantity: "",
      cartProductId: product.id,
      dispatchTime: dispatchTime?.deliveryTermStandardMin + "-" + dispatchTime?.deliveryTermStandardMax,
    });
    // services.cart.updateItem(...)
  };

  const removeProduct = () => {
    setRemoveConfirmOpen(false);
    Gtag.pushEvent("remove_item", product.id);
    Gtag.pushEventEcommerce("remove_from_cart", {
      items: [
        {
          item_name: getItemName(product?.product),
          item_id: product?.product.id,
          price: parseFloat(product?.price.amount),
          item_brand: product?.product.brand.name,
          index: 0,
          quantity: product.quantity,
          ...getCategoriesEcommerceItems(product.product.categories, services),
        },
      ],
    });

    services.cart.updateItem({
      productId: product!.product.id,
      cartProductId: product!.id,
      quantity: 0,
      priceId: product!.priceId,
    });
  };

  const priceInfoRender = () => {
    const originPrice = price.discount ? (
      <div className={styles["origin-price"]}>
        <PriceValue className={styles.price} amount={price.amount} />
      </div>
    ) : null;
    const salePrice = (
      <div className={styles["sale-price"]}>
        <PriceValue className={styles.price} amount={price.total} />
      </div>
    );
    const perUnitPrice = (
      <div className={styles["per-unit-price"]}>
        <PriceValue className={styles.price} amount={price.price} /> {t("fhy6ujk03k55lp9r.per-unit", "per unit")}
      </div>
    );
    return (
      <>
        {perUnitPrice}
        {originPrice}
        {salePrice}
      </>
    );
  };

  const dispatchTimeRender = () => {
    if (product.product.prices.length > 1 && !product.errors?.some((error) => ["disabled", "no-price", "change-count"].includes(error.type))) {
      return (
        <FixButton className={styles["price-selector-btn"]} variant="text" onClick={() => setPricesOpened((v) => !v)}>
          {deliveryTermsRange(currentPrice)}
          <Icon name="chevron-down" />
        </FixButton>
      );
    }
    return <span className={styles["price-selector-btn"]}>{deliveryTermsRange(currentPrice)}</span>;
  };

  const pricesListRender = () => (
    <div className={styles["prices-list"]}>
      {product.product.prices.map((item) => (
        <FixButton
          key={item.id}
          onClick={() => changePrice(item.id)}
          className={classNames(styles["price-btn"], {
            [styles.selected]: currentPrice?.id === item.id,
          })}
        >
          {deliveryTermsRange(item)}
        </FixButton>
      ))}
    </div>
  );

  const notifyRender = () => {
    if (!product.errors || !product.errors[0]?.message) return null;
    const notify = product.errors[0];
    const message = notify.message;
    // Add priceChanged logic if needed
    return <CartProductNotify text={message} />;
  };

  const notifyTitleRender = () => {
    if (!product.errors || !product.errors[0]?.title) return null;
    const colorType = "error";
    // Add logic for successTypes, neutralTypes if needed
    return <CartProductNotifyTitle text={product.errors[0].title} colorType={colorType} />;
  };

  const renderAdditionalExchangeMessage = () => {
    if (singleProduct && isOverMaxQuantity && product.product?.exchangesFinal && product.product?.exchangesFinal.length === 1) {
      return (
        <ExchangeOffer
          shortProduct={product.product.exchangesFinal[0]}
          dispatchSelectedQuantity={product.maxOrderQuantity}
          afterExchangeAdded={() => setIsOverMaxQuantity(false)}
        />
      );
    }
    return null;
  };

  const getMaxQuantity = currentPrice?.availableStockCount ? product.maxOrderQuantity : null;
  const getMinQuantity = !currentPrice?.availableStockCount ? product.minOrderQuantity : null;

  return (
    <div className={classNames([styles.root, styles[layout ?? layoutStyleType]])} data-layout={layout}>
      {removeConfirmOpen && (
        <ConfirmDialog
          text={t("cart.remove-product-confirm", "Do you really want to delete this item from your basket?")}
          onAccept={removeProduct}
          onCancel={() => setRemoveConfirmOpen(false)}
        />
      )}
      <div
        className={classNames(styles.container, {
          [styles.disabled]: !!product.isDisabled,
          [styles["single-border"]]: singleProduct,
        })}
      >
        <div className={styles.content}>
          <div className={styles["product-img"]}>{imgUrl ? <img src={imgUrl} alt="" /> : <FixImage src={noImage} />}</div>
          <div className={styles.info}>
            <div className={styles["product-header"]}>
              <div className={styles["product-info"]}>
                <div className={styles.title}>
                  <a
                    className={styles["product-link"]}
                    href={createProductUrl(product.product, services.categories)}
                    onClick={() => googleEvent("product_click", product.product.id)}
                  >
                    {product?.product.mpnFormatted} {product?.product.brand.name} {product?.product.productInfo.name}
                  </a>
                </div>
                <div className={styles.category}>{product?.product.categories?.[0].name}</div>
                {notifyTitleRender()}
                <div className={styles.dispatch}>
                  <span>{t("bs6v90iqxu790208.dispatch-time", "Dispatch time")}:</span>
                  {dispatchTimeRender()}
                </div>
                {pricesOpened && pricesListRender()}
              </div>
              <div className={styles["product-info-right"]}>
                <div className={styles["product-quantity"]}>
                  <FixQuantity
                    className={styles.quantity}
                    quantity={quantity}
                    minQuantity={getMinQuantity}
                    maxQuantity={getMaxQuantity}
                    factorQuantity={product?.factorOrderQuantity}
                    disabled={product.isDisabled}
                    beforeUpdateQuantity={beforeSetQuantity}
                    onUpdateQuantity={handleQuantityChange}
                  />
                  <div className={styles["product-moq"]}>
                    {t("3370g8gw7u3eeieh.moq", "MOQ")}: {product.minOrderQuantity}
                  </div>
                </div>
                <div className={styles["price-info"]}>{priceInfoRender()}</div>
              </div>
            </div>
            {specifications.length > 0 && (
              <div className={styles["product-footer"]}>
                <div className={styles.actions}>
                  <FixButton variant="text" className={styles["more-btn"]} onClick={() => setSpecificationsOpened((v) => !v)}>
                    {t("eb32219bfq9o6ph1.more-info", "More info")}
                    <Icon name="chevron-down" />
                  </FixButton>
                </div>
              </div>
            )}
            {specificationsOpened && <ProductSpecifications options={specifications} />}
          </div>
          <div className={styles["product-remove"]}>
            <FixButton variant="text" className={styles["remove-btn"]} onClick={() => setRemoveConfirmOpen(true)}>
              <Icon src={close} />
            </FixButton>
          </div>
        </div>
        {notifyRender()}
      </div>
      {renderAdditionalExchangeMessage()}
    </div>
  );
};

export default CartItem;
